import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

import {
  AddProviderFormStyled,
  FormTitleStyled,
  FormAvatarBoxStyled,
  FormElementStyled,
  FormInputFieldWrapperStyled,
} from './AddProviderStyled';
import { AddProvidersProps } from '../../../types';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import { PsCheckboxTuner, PsTextTuner } from 'stencilSetup';
import './addProviders.css';
import { getDependentAge } from 'enrollment/features/Shared/FamilySelect/utils';

export const AddProviders = ({
  subscriber,
  pageText,
  pcpFormData,
  handlePcpFormChange,
  isPcpNameRequired,
  isPcpCodeRequired,
  isPcpCodeNone,
  isPcpNameNone,
}: AddProvidersProps) => {
  const { t } = useTranslation('enrollment.pcpDrawer');

  if (!pcpFormData) {
    return;
  }

  const sanitizedHtml = pageText?.data?.page_text
    ? DOMPurify.sanitize(pageText?.data?.page_text, { RETURN_TRUSTED_TYPE: true })
    : '';

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} className="mt-2 mb-5"></div>
      <div className="my-2" data-testid="add-providers-form">
        <FormTitleStyled style={{ textTransform: 'none' }}>
          {t('addProviders.title')}
        </FormTitleStyled>
        <AddProviderFormStyled>
          <FormElementStyled>
            <FormAvatarBoxStyled>
              {displayDependentAvatar(subscriber, 40)}
              <span className="body2 text-capitalize">you</span>
            </FormAvatarBoxStyled>
            <FormInputFieldWrapperStyled>
              {!isPcpNameNone && (
                <PsTextTuner
                  data-testid="pcp-name-field"
                  inputId="pcp-name-you"
                  name="pcpName"
                  label={t('addProviders.providerName')}
                  type="text"
                  value={pcpFormData?.pcpName}
                  required={isPcpNameRequired}
                  onInput={(e) => handlePcpFormChange(true, e)}
                />
              )}

              {!isPcpCodeNone && (
                <PsTextTuner
                  data-testid="pcp-code-field"
                  inputId="pcp-code-you"
                  name="pcpCode"
                  label={t('addProviders.providerNetwork')}
                  type="text"
                  value={pcpFormData?.pcpCode}
                  onInput={(e) => handlePcpFormChange(true, e)}
                  required={isPcpCodeRequired}
                />
              )}

              <PsCheckboxTuner
                inputId="existing-patient-you"
                label={t('addProviders.existingPatient')}
                name="existingPatient"
                checked={pcpFormData?.existingPatient}
                onInput={(e) => handlePcpFormChange(true, e)}
                labelClass="ml-n1 checkbox-left mb-md-4"
              />
            </FormInputFieldWrapperStyled>
          </FormElementStyled>

          {pcpFormData?.dependents &&
            pcpFormData?.dependents.map((dependent: any, index: number) => (
              <FormElementStyled key={index}>
                <FormAvatarBoxStyled>
                  {displayDependentAvatar(dependent.dependentProfile, 40)}
                  <span className="body2 truncate text-capitalize">
                    {dependent.firstName}
                  </span>
                  <span className="body2">
                    {dependent?.birthDate
                      ? ` (${getDependentAge(dependent.birthDate)})`
                      : ''}
                  </span>
                </FormAvatarBoxStyled>
                <FormInputFieldWrapperStyled>
                  {!isPcpNameNone && (
                    <PsTextTuner
                      data-testid="pcp-name-field"
                      inputId={`pcp-name-dep-${index + 1}`}
                      name="pcpName"
                      label={t('addProviders.providerName')}
                      type="text"
                      value={dependent.pcpName}
                      onInput={(e) =>
                        handlePcpFormChange(false, e, dependent.id)
                      }
                      required={isPcpNameRequired}
                    />
                  )}
                  {!isPcpCodeNone && (
                    <PsTextTuner
                      data-testid="pcp-code-field"
                      inputId={`pcp-provider-dep-${index + 1}`}
                      name="pcpCode"
                      label={t('addProviders.providerNetwork')}
                      type="text"
                      value={dependent.pcpCode}
                      onInput={(e) =>
                        handlePcpFormChange(false, e, dependent.id)
                      }
                      required={isPcpCodeRequired}
                    />
                  )}

                  <PsCheckboxTuner
                    data-testid={`pcp-existing-patient-dep-${index + 1}`}
                    inputId={`pcp-existing-patient-dep-${index + 1}`}
                    name="existingPatient"
                    label={t('addProviders.existingPatient')}
                    checked={dependent.existingPatient}
                    onInput={(e) => handlePcpFormChange(false, e, dependent.id)}
                    labelClass="ml-n1 mb-md-4"
                  />
                </FormInputFieldWrapperStyled>
              </FormElementStyled>
            ))}
        </AddProviderFormStyled>
      </div>
    </>
  );
};
